import { FormArray, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import {
  CheckoutProductUpgradeExtension,
  CheckoutExtensionUI,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DynamicRule, RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '../../../shared/all-extension-rules';

export type DisplayMode = `${CheckoutProductUpgradeExtension.DisplayMode}`;
export type ProductsInCartMode = `${CheckoutProductUpgradeExtension.ProductsInCartMode}`;
export type UpgradeProductsMode = `${CheckoutProductUpgradeExtension.UpgradeProductsMode}`;
export type VariantSelectorType = CheckoutProductUpgradeExtension.VariantSelectorType;
export type UpgradeProduct = CheckoutProductUpgradeExtension.UpgradeProduct & { sku?: string; price?: string };
export type UpgradeRecommendedProducts = CheckoutProductUpgradeExtension.UpgradeRecommendedProducts;
export type UpgradeProductsInCart = CheckoutProductUpgradeExtension.UpgradeProductsInCart;
export type ProductSelection = CheckoutProductUpgradeExtension.ProductSelection;
export type HideProductsAlreadyInCart = CheckoutProductUpgradeExtension.HideProductsAlreadyInCart;
export type ProductsMatchMode = CheckoutProductUpgradeExtension.ProductsMatchMode;
export type NoMatchingPlanBehavior = CheckoutProductUpgradeExtension.NoMatchingPlanBehavior;
export type SubscriptionFrequency = CheckoutProductUpgradeExtension.SubscriptionFrequency;
export type UpgradeType = CheckoutProductUpgradeExtension.UpgradeType;
export type LayoutStyle = CheckoutProductUpgradeExtension.LayoutStyle;
export type SubscriptionUpgradeBehavior = CheckoutProductUpgradeExtension.SubscriptionUpgradeBehavior;
export type CompletionBannerTone = CheckoutProductUpgradeExtension.CompletionBannerTone;

export type BadgeValue = CheckoutProductUpgradeExtension.Badge;

export const DEFAULT_BADGE: BadgeValue = {
  enabled: false,
  text: '',
  backgroundColor: '#FD3458',
  textColor: '#FFFFFF',
  cornerRadius: CheckoutExtensionUI.CornerRadius.SMALL,
  icon: undefined,
};

export const DEFAULT_UPGRADE_RULE_GROUP: Omit<ProductSelection, 'id'> = {
  productsInCart: {
    mode: CheckoutProductUpgradeExtension.ProductsInCartMode.MANUAL,
    rules: [],
    productsMatchMode: 'all',
  },
  upgradeProducts: {
    mode: CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL,
    products: [],
    rules: [],
    upgradeType: 'product',
    subscriptionUpgradeBehavior: 'oneTime',
    groupByKeyEnabled: false,
    groupByKey: '',
  },
  inStockProductsOnly: true,
  matchFulfillmentCenterToCartItems: false,
  includeDigitalItems: true,
  hideProductsAlreadyInCart: 'parentProduct',
};

export const DEFAULT_UPGRADE_VALUES: CheckoutProductUpgradeExtension.Template = {
  internalName: 'Product Upgrade',
  settings: {
    displayMode: CheckoutProductUpgradeExtension.DisplayMode.LINE_ITEM_ACTION,
    variantSelector: 'inside',
    allowRevert: false,
    revertButtonLabel: 'Revert',
    allowSubscriptions: false,
    subscriptionFrequency: 'strict',
    noMatchingPlanBehavior: 'useFirstAvailable',
    layoutStyle: 'comparison',
  },
  appearance: {
    titleText: {
      label: 'Upgrade Available',
      appearance: CheckoutExtensionUI.Appearance.BASE,
      badge: { ...DEFAULT_BADGE },
    },
    upgradeText: {
      label: 'Upgrade',
      appearance: CheckoutExtensionUI.Appearance.ACCENT,
      badge: { ...DEFAULT_BADGE },
    },
    button: {
      label: 'Upgrade',
      labelForProductsWithVariants: 'Select an option to upgrade',
      appearance: CheckoutExtensionUI.Appearance.INTERACTIVE,
    },
    mainBackground: {
      border: CheckoutExtensionUI.BorderType.NONE,
      borderWidth: CheckoutExtensionUI.BorderWidth.BASE,
      cornerRadius: CheckoutExtensionUI.CornerRadius.NONE,
      padding: CheckoutExtensionUI.Padding.BASE,
    },
    image: {
      border: CheckoutExtensionUI.BorderType.NONE,
      borderWidth: CheckoutExtensionUI.BorderWidth.BASE,
      cornerRadius: CheckoutExtensionUI.CornerRadius.NONE,
      padding: CheckoutExtensionUI.Padding.BASE,
    },
    completionBanner: {
      enabled: false,
      text: '',
      tone: 'success',
    },
  },
  productSelection: [],
  restrictionsRules: [],
};

export interface TypedRuleGroup {
  entity: FormControl<string | null>;
  entityOperator: FormControl<string | null>;
  entityParams: UntypedFormGroup;
  groupIndex?: FormControl<number | undefined | null>;
}

export interface SettingsFormGroup {
  displayMode: FormControl<CheckoutProductUpgradeExtension.DisplayMode>;
  variantSelector: FormControl<VariantSelectorType>;
  allowRevert: FormControl<boolean>;
  revertButtonLabel: FormControl<string>;
  allowSubscriptions: FormControl<boolean>;
  subscriptionFrequency: FormControl<SubscriptionFrequency>;
  noMatchingPlanBehavior: FormControl<NoMatchingPlanBehavior>;
  layoutStyle: FormControl<LayoutStyle>;
}

export interface BadgeFormGroup {
  enabled: FormControl<boolean>;
  text: FormControl<string>;
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  cornerRadius: FormControl<CheckoutExtensionUI.CornerRadius>;
  icon: FormControl<string | null>;
}

export interface TextAppearanceFormGroup {
  label: FormControl<string>;
  appearance: FormControl<CheckoutExtensionUI.Appearance>;
  badge: FormGroup<BadgeFormGroup>;
}

export interface ButtonAppearanceFormGroup {
  label: FormControl<string>;
  labelForProductsWithVariants: FormControl<string>;
  appearance: FormControl<CheckoutExtensionUI.Appearance>;
}

export interface BackgroundStylingFormGroup {
  border: FormControl<CheckoutExtensionUI.BorderType>;
  borderWidth: FormControl<CheckoutExtensionUI.BorderWidth>;
  cornerRadius: FormControl<CheckoutExtensionUI.CornerRadius>;
  padding: FormControl<CheckoutExtensionUI.Padding>;
}

export interface CompletionBannerFormGroup {
  enabled: FormControl<boolean>;
  text: FormControl<string>;
  tone: FormControl<CompletionBannerTone>;
}

export interface AppearanceFormGroup {
  titleText: FormGroup<TextAppearanceFormGroup>;
  upgradeText: FormGroup<TextAppearanceFormGroup>;
  button: FormGroup<ButtonAppearanceFormGroup>;
  mainBackground: FormGroup<BackgroundStylingFormGroup>;
  image: FormGroup<BackgroundStylingFormGroup>;
  completionBanner: FormGroup<CompletionBannerFormGroup>;
}

export interface ProductsInCartFormGroup {
  mode: FormControl<CheckoutProductUpgradeExtension.ProductsInCartMode>;
  rules: FormArray<FormGroup<TypedRuleGroup>>;
  products: FormControl<UpgradeProduct[]>;
  productsMatchMode: FormControl<CheckoutProductUpgradeExtension.ProductsMatchMode>;
}

export interface UpgradeProductsFormGroup {
  mode: FormControl<CheckoutProductUpgradeExtension.UpgradeProductsMode>;
  products: FormControl<UpgradeProduct[]>;
  rules: FormArray<FormGroup<TypedRuleGroup>>;
  upgradeType: FormControl<UpgradeType>;
  subscriptionUpgradeBehavior: FormControl<SubscriptionUpgradeBehavior>;
  groupByKeyEnabled: FormControl<boolean>;
  groupByKey: FormControl<string>;
}

export interface ProductSelectionFormGroup {
  id: FormControl<number>;
  productsInCart: FormGroup<ProductsInCartFormGroup>;
  upgradeProducts: FormGroup<UpgradeProductsFormGroup>;
  inStockProductsOnly: FormControl<boolean>;
  matchFulfillmentCenterToCartItems: FormControl<boolean>;
  includeDigitalItems: FormControl<boolean>;
  hideProductsAlreadyInCart: FormControl<HideProductsAlreadyInCart | null>;
}

export const VARIANT_SELECTOR_OPTIONS = [
  { value: 'inside', label: 'Always Show', helpText: 'Dropdown appears when clicking the main CTA button' },
  { value: 'outside', label: 'Popup', helpText: 'Dropdown is always visible above or next to the main CTA button' },
];

export interface LayoutSnapshot {
  displayMode: CheckoutProductUpgradeExtension.DisplayMode;
  layoutStyle: LayoutStyle;
  allowRevert: boolean;
}

export function isFocusedOfferLayout(settings: Pick<LayoutSnapshot, 'displayMode' | 'layoutStyle'>): boolean {
  return (
    settings.displayMode === CheckoutProductUpgradeExtension.DisplayMode.STANDALONE_BLOCK &&
    settings.layoutStyle === 'focused'
  );
}

export interface UpgradeFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  restrictionsRules: FormArray<FormGroup<TypedRuleGroup>>;
  settings: FormGroup<SettingsFormGroup>;
  appearance: FormGroup<AppearanceFormGroup>;
  productSelection: FormArray<FormGroup<ProductSelectionFormGroup>>;
}

export function createRuleGroupFormGroup(
  id: number,
  values: Partial<CheckoutProductUpgradeExtension.ProductSelection>,
  rulesDynamicFormService: RulesDynamicFormService,
): FormGroup<ProductSelectionFormGroup> {
  const defaultValues = values || { ...DEFAULT_UPGRADE_RULE_GROUP, id };
  const productsInCartRules = getExtensionRules();
  const upgradeProductsRules = getExtensionRules({
    onlyRules: [
      RuleEntity.PRODUCT_TYPE,
      RuleEntity.PRODUCT_TAG,
      RuleEntity.SKU,
      RuleEntity.BARCODE,
      RuleEntity.COLLECTION,
      RuleEntity.VENDOR,
    ],
  });

  return new FormGroup<ProductSelectionFormGroup>({
    id: new FormControl(defaultValues.id ?? id),
    productsInCart: new FormGroup({
      mode: new FormControl(
        defaultValues.productsInCart?.mode ?? CheckoutProductUpgradeExtension.ProductsInCartMode.SMART,
      ),
      rules: rulesDynamicFormService.initRulesFormArray(
        defaultValues.productsInCart?.rules?.flat() ?? [],
        productsInCartRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
      products: new FormControl(defaultValues.productsInCart?.products ?? []),
      productsMatchMode: new FormControl(defaultValues.productsInCart?.productsMatchMode ?? 'all'),
    }),
    upgradeProducts: new FormGroup({
      mode: new FormControl(
        defaultValues.upgradeProducts?.mode ?? CheckoutProductUpgradeExtension.UpgradeProductsMode.MANUAL,
      ),
      products: new FormControl(defaultValues.upgradeProducts?.products ?? []),
      rules: rulesDynamicFormService.initRulesFormArray(
        defaultValues.upgradeProducts?.rules?.flat() ?? [],
        upgradeProductsRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
      upgradeType: new FormControl<UpgradeType>(defaultValues.upgradeProducts?.upgradeType ?? 'product'),
      subscriptionUpgradeBehavior: new FormControl<SubscriptionUpgradeBehavior>(
        defaultValues.upgradeProducts?.subscriptionUpgradeBehavior ?? 'oneTime',
      ),
      groupByKeyEnabled: new FormControl<boolean>(defaultValues.upgradeProducts?.groupByKeyEnabled ?? false),
      groupByKey: new FormControl<string>(defaultValues.upgradeProducts?.groupByKey ?? ''),
    }),
    inStockProductsOnly: new FormControl(defaultValues.inStockProductsOnly ?? true),
    matchFulfillmentCenterToCartItems: new FormControl(defaultValues.matchFulfillmentCenterToCartItems ?? false),
    includeDigitalItems: new FormControl(defaultValues.includeDigitalItems ?? true),
    hideProductsAlreadyInCart: new FormControl(defaultValues.hideProductsAlreadyInCart ?? 'parentProduct'),
  });
}
